/**
 * Page de liste des garanties avec filtres et pagination
 * Composant partagé utilisé par outil-pro et admin
 */

import { useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Copy, Shield, Grid, Plus, Loader2, Info } from 'lucide-react';
import { useWarranties, useWarrantyFilterFacets } from '../hooks/useWarranties';
import { warrantyApi } from '../api/warrantyApi';
import {
  useActivateWarranty,
  useDeactivateWarranty,
  useUpdateWarranty,
  useDuplicateWarranty,
  useCopyWarrantiesToSociety,
} from '../hooks/useWarrantyMutations';
import { useWarrantyReorder } from '../hooks/useWarrantyReorder';
import { WarrantiesGridView } from './WarrantiesGridView';
import {
  getWarrantyTypeLabel,
  getVehicleTypeLabel,
  getDurationLabel,
  formatPriceTTC,
} from '../utils/warrantyFormatter';
import { calculateMonthlyBreakdown } from '../services/pricingCalculator';
import { WARRANTY_TYPES, WARRANTY_TYPE_LABELS, VEHICLE_TYPE_LABELS, DURATION_LABELS } from '../types/warranty.types';
import { useNotifications } from '@/contexts/NotificationContext';
import { Can } from '@/Components/auth/Can';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { DataList } from '@/Components/shared/List';
import { useListState } from '@/hooks/list';
import type { ColumnDef, RowAction, BaseListFilters } from '@/types/list.types';
import { FilterInlineSelectInput } from '@/Components/shared/FilterInlineSelectInput';
import { FormInput } from '@/Components/shared/FormInput';
import { useConfirm } from '@/hooks/useConfirm';
import { useAllowedDurations } from '../hooks/useAllowedDurations';
import { useAllowedVehicleTypes } from '../hooks/useAllowedVehicleTypes';
import { useEffectiveSocietyId } from '../hooks/useEffectiveSocietyId';
import { useIsWarrantyPolicyOwner } from '../hooks/useIsWarrantyPolicyOwner';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { useSocietyVendorType } from '@/features/societies/hooks/useSocietyVendorType';
import { ColorPickerPopover } from './ColorPickerPopover';
import { AutocompleteSelect } from '@/Components/shared/AutocompleteSelect';
import type { SocietyListItem } from '@/features/societies/types/society.types';
import { useCurrentUserWording } from '@/hooks/useVendorWording';
import { usePermissions } from '@/hooks/auth/usePermissions';
import { useAssistanceVisibility } from '@/features/assistance/hooks/useAssistanceVisibility';
import { AssistanceBadge } from '@/features/assistance/components/AssistanceBadge';
import { ASSISTANCE_LABEL } from '@/features/assistance/assistanceWording';

interface WarrantyFilters extends BaseListFilters {
  search?: string;
  warrantyType?: number;
  vehicleType?: number;
  isActive?: boolean;
  isBroadcast?: boolean;
  /** Présence d'une formule d'assistance, filtrée côté serveur. */
  hasAssistance?: boolean;
  duration?: number;
  societyId?: string;
}

interface Warranty {
  uuid: string;
  code: string;
  name: string;
  warrantyType: number;
  vehicleType: number;
  duration: number;
  kmLimit: number | null;
  financialCap: number;
  sellingPrice: number;
  partsCount: number;
  /**
   * Formule d'assistance retenue (`null` = aucune). Déjà servi par l'endpoint ;
   * déclaré explicitement plutôt que laissé à l'index signature ci-dessous, la
   * colonne dédiée s'appuyant dessus.
   */
  assistanceFormulaId: number | null;
  headTransferPrice: number | null;
  memberTransferPrice: number | null;
  isMensualisable: boolean;
  isCashPayment: boolean;
  monthlyPlateformFees: number;
  isActive: boolean;
  isBroadcast: boolean;
  societyName: string;
  color?: string;
  order?: number;
  [key: string]: any;
}

const WARRANTY_TYPE_BADGE_CLASSES: Record<number, string> = {
  [WARRANTY_TYPES.WARRANTY]: 'tw-bg-blue-100 tw-text-blue-800',
  [WARRANTY_TYPES.SERVICE]: 'tw-bg-purple-100 tw-text-purple-800',
  [WARRANTY_TYPES.EXTENSION]: 'tw-bg-gray-100 tw-text-gray-700',
};

interface WarrantiesListPageProps {
  onRowClick?: (uuid: string) => void;
  onCreateClick?: () => void;
  headerActions?: ReactNode;
  /** When provided, locks the society filter to this ID (embedded mode in society detail) */
  societyId?: string;
  /** Embedded mode (no outer padding/margins). Default: false */
  embedded?: boolean;
  /** Position du toggle de vue : 'left' (défaut) ou 'right' */
  viewTogglePosition?: 'left' | 'right';
  /** Masquer le titre dans le DataList (le parent gère son propre header) */
  hideTitle?: boolean;
  /** Callback pour remonter le total au parent */
  onTotalChange?: (total: number) => void;
  /** ID de la société parent (pour le bouton "Copier les garanties du groupe") */
  copyFromSocietyId?: string;
  /**
   * Société consultée par un EXTERNAL multi-société (sélecteur de l'onglet
   * « Stratégie »). Remplace sa société principale sans activer le mode
   * embarqué admin de `societyId`. Défaut : société principale.
   */
  scopeSocietyId?: string;
}

export const WarrantiesListPage = ({ onRowClick, onCreateClick, headerActions, societyId: fixedSocietyId, embedded, viewTogglePosition, hideTitle = false, onTotalChange, copyFromSocietyId, scopeSocietyId }: WarrantiesListPageProps) => {
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  const { currentUser } = useAuthStore();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const wording = useCurrentUserWording();
  const copyWarranties = useCopyWarrantiesToSociety();

  const showSocietyFilter = useMemo(() => {
    return !fixedSocietyId && currentUser?.type === 'INTERNAL';
  }, [currentUser?.type, fixedSocietyId]);

  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
    resetFilters,
    activeFiltersCount,
  } = useListState<WarrantyFilters>({
    defaultLimit: 20,
    defaultSortBy: 'order',
    defaultSortDirection: 'ASC',
    persistInUrl: true,
    filterTypes: {
      warrantyType: 'number',
      vehicleType: 'number',
      duration: 'number',
      isActive: 'boolean',
      isBroadcast: 'boolean',
      // Sans ce typage, `hasAssistance=false` se réhydrate depuis l'URL en
      // chaîne `'false'` — truthy — et le filtre s'inverserait en silence au
      // rechargement de la page.
      hasAssistance: 'boolean',
    },
  }, { isActive: true, isBroadcast: true });

  const externalSocietyId = currentUser?.type === 'EXTERNAL' ? scopeSocietyId ?? currentUser.societyId : undefined;
  const rawSocietyId = fixedSocietyId || externalSocietyId;
  const effectiveSocietyId = useEffectiveSocietyId(rawSocietyId);
  const isWarrantyPolicyOwner = useIsWarrantyPolicyOwner(rawSocietyId);
  const { can } = usePermissions();
  const { data: config } = useEffectiveConfiguration(rawSocietyId);
  // Même société que la configuration effective ci-dessus : celle AFFICHÉE.
  // Aucune requête supplémentaire, React Query dédoublonne sur la même clé.
  const { isVisible: isAssistanceVisible } = useAssistanceVisibility(rawSocietyId);
  const showTransferPrices = !rawSocietyId || !!config?.isTransferPrice;
  const societyHasStripe = !!config?.stripeAccountId;
  const { data: policyOwnerSociety } = useSociety(!isWarrantyPolicyOwner ? config?.warrantyPolicyOwnerId ?? undefined : undefined);

  const effectiveFilters = useMemo(() => {
    // La diffusion reste forcée pour tout le monde sauf les INTERNAL : eux
    // peuvent la lever pour retrouver — et rattraper — une garantie créée non
    // diffusée, qui est autrement introuvable dans toute l'application.
    const isInternal = currentUser?.type === 'INTERNAL';
    const base = { ...filters, isBroadcast: isInternal ? filters.isBroadcast ?? true : true };
    // Non-owner ne voit que les garanties actives (sauf INTERNAL)
    if (!isWarrantyPolicyOwner && currentUser?.type !== 'INTERNAL') {
      base.isActive = true;
    }
    if (fixedSocietyId && effectiveSocietyId) {
      return { ...base, societyId: effectiveSocietyId };
    }
    if (currentUser?.type === 'EXTERNAL' && effectiveSocietyId) {
      return { ...base, societyId: effectiveSocietyId };
    }
    return base;
  }, [filters, currentUser?.type, effectiveSocietyId, fixedSocietyId, isWarrantyPolicyOwner]);

  const { canChooseWarrantyType } = useSocietyVendorType(effectiveFilters.societyId);

  const { allowedVehicleTypes } = useAllowedVehicleTypes(effectiveFilters.societyId);
  const allowedDurations = useAllowedDurations(effectiveFilters.societyId);

  const { data: facets } = useWarrantyFilterFacets(effectiveFilters.societyId);

  // Options de filtres croisées : config (allowed) ∩ données réelles (facets)
  const facetWarrantyTypes = useMemo(() => {
    if (!facets) return canChooseWarrantyType ? Object.keys(WARRANTY_TYPE_LABELS).map(Number) : [];
    const types = facets.warrantyTypes;
    return canChooseWarrantyType ? types : types.filter((t) => t === 0);
  }, [facets, canChooseWarrantyType]);

  const facetVehicleTypes = useMemo(() => {
    if (!facets) return allowedVehicleTypes;
    return allowedVehicleTypes.filter((t) => facets.vehicleTypes.includes(t));
  }, [facets, allowedVehicleTypes]);

  const facetDurations = useMemo(() => {
    if (!facets) return allowedDurations;
    return allowedDurations.filter((d) => facets.durations.includes(d));
  }, [facets, allowedDurations]);

  const facetStatuses = useMemo(() => {
    if (!facets) return [true, false];
    return facets.statuses;
  }, [facets]);

  /**
   * Colonne et filtre « Affinicar Assist » : réservés aux utilisateurs concernés
   * par l'offre, et seulement là où au moins une garantie en porte une —
   * ailleurs la colonne n'afficherait qu'une suite de « Non ».
   *
   * Masqué tant que les facettes sont en vol, pour ne pas afficher puis retirer
   * la colonne. L'optionnalité sur `hasAssistance` couvre une API pas encore
   * déployée, qui ne renvoie pas encore cette facette.
   */
  const showAssistance = useMemo(
    () => isAssistanceVisible && !!facets?.hasAssistance?.includes(true),
    [isAssistanceVisible, facets],
  );

  const { data, isLoading, error } = useWarranties(effectiveFilters);

  // Compteurs actifs/inactifs (uniquement en mode embedded)
  const activeCountFilters = useMemo(
    () => ({ ...effectiveFilters, isActive: true, page: 1, limit: 1 }),
    [effectiveFilters],
  );
  const inactiveCountFilters = useMemo(
    () => ({ ...effectiveFilters, isActive: false, page: 1, limit: 1 }),
    [effectiveFilters],
  );
  const { data: activeData } = useQuery({
    queryKey: ['warranties', activeCountFilters],
    queryFn: () => warrantyApi.getWarranties(activeCountFilters),
    enabled: !!fixedSocietyId,
    staleTime: 30000,
  });
  const { data: inactiveData } = useQuery({
    queryKey: ['warranties', inactiveCountFilters],
    queryFn: () => warrantyApi.getWarranties(inactiveCountFilters),
    enabled: !!fixedSocietyId,
    staleTime: 30000,
  });

  const duplicateWarranty = useDuplicateWarranty();
  const activateWarranty = useActivateWarranty();
  const deactivateWarranty = useDeactivateWarranty();
  const updateWarranty = useUpdateWarranty();

  const warranties = useMemo(() => {
    const all = data?.warranties || [];
    // Ne masquer les garanties mensuel-only que dans un contexte d'une société précise
    // (EXTERNAL ou fixedSocietyId). Pour la liste globale admin (rawSocietyId undefined),
    // on affiche tout car chaque garantie appartient à une société potentiellement différente.
    if (rawSocietyId && !societyHasStripe) {
      return all.filter((w) => !(w.isMensualisable && !w.isCashPayment));
    }
    return all;
  }, [data, rawSocietyId, societyHasStripe]);
  const total = useMemo(() => data?.total || 0, [data]);

  // Déclaré après `warranties` : le hook a besoin de la liste réellement
  // affichée pour résoudre le repère du déplacement.
  const { reorder } = useWarrantyReorder(warranties);

  useEffect(() => { onTotalChange?.(total); }, [total, onTotalChange]);

  const hasVisibleFilters = useMemo(() => {
    // Le filtre Diffusion est toujours rendu pour un INTERNAL : il ne dépend
    // d'aucune facette, puisqu'il sert justement à faire apparaître des lignes
    // que la liste n'a jamais remontées.
    return currentUser?.type === 'INTERNAL' || showSocietyFilter || showAssistance || facetWarrantyTypes.length > 1 || facetVehicleTypes.length > 1 || facetDurations.length > 1 || facetStatuses.length > 1;
  }, [currentUser?.type, showSocietyFilter, showAssistance, facetWarrantyTypes, facetVehicleTypes, facetDurations, facetStatuses]);

  // Deux questions distinctes, volontairement séparées : « cet utilisateur
  // a-t-il le droit de réordonner ? » et « l'état de la liste le permet-il ? ».
  // Les mêler faisait diverger le bandeau explicatif de la condition réelle.
  const isReorderAllowedForUser = useMemo(() => {
    if (!isWarrantyPolicyOwner && currentUser?.type !== 'INTERNAL') return false;
    if (!can('warranty.reorder')) return false;
    if (currentUser?.type === 'INTERNAL') {
      return !!(filters.societyId || fixedSocietyId);
    }
    return true;
  }, [currentUser?.type, filters.societyId, fixedSocietyId, isWarrantyPolicyOwner, can]);

  // Réordonner n'a de sens que si la liste est affichée dans l'ordre qu'on
  // manipule : triée par nom ou par prix, l'action serait enregistrée sans
  // effet visible, ce que l'utilisateur lirait comme un échec.
  //
  // La pagination, elle, n'est PAS bloquante : le déplacement s'exprime par
  // rapport à une voisine réelle, donc glisser une carte sur une autre au sein
  // d'une même page reste exact quel que soit le nombre de pages. Seule la
  // portée est limitée — on ne peut pas viser une position non affichée.
  const reorderBlockedReason = useMemo(() => {
    if (sortConfig.sortBy !== 'order' || sortConfig.sortDirection !== 'ASC') {
      return 'Le glisser-déposer est désactivé tant que la liste est triée par une autre colonne.';
    }
    return null;
  }, [sortConfig.sortBy, sortConfig.sortDirection]);

  const canReorder = isReorderAllowedForUser && !reorderBlockedReason;

  const handleNavigate = (uuid: string) => {
    if (onRowClick) {
      onRowClick(uuid);
    } else {
      navigate(`/warranties/${uuid}`);
    }
  };

  const handleDuplicate = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: `Dupliquer ${wording.withArticle}`,
      message: `Êtes-vous sûr de vouloir dupliquer ${wording.withArticle} "${name}" ?`,
      confirmLabel: 'Dupliquer',
      cancelLabel: 'Annuler',
      variant: 'info',
    });

    if (!confirmed) return;

    try {
      await duplicateWarranty.mutateAsync({ id, newName: `${name} (Copie)` });
      showNotification('success', `${wording.singular} dupliquée avec succès`);
    } catch (err) {
      showNotification('error', 'Erreur lors de la duplication');
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    const action = isActive ? 'désactiver' : 'activer';
    const confirmed = await confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${wording.withArticle}`,
      message: `Êtes-vous sûr de vouloir ${action} cette ${wording.singularLower} ?`,
      confirmLabel: action.charAt(0).toUpperCase() + action.slice(1),
      cancelLabel: 'Annuler',
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      if (isActive) {
        await deactivateWarranty.mutateAsync(id);
        showNotification('success', `${wording.singular} désactivée`);
      } else {
        await activateWarranty.mutateAsync(id);
        showNotification('success', `${wording.singular} activée`);
      }
    } catch (err) {
      showNotification('error', "Erreur lors de la modification du statut");
    }
  };

  const columns: ColumnDef<Warranty>[] = [
    {
      key: 'societyName',
      label: 'Société',
      render: (warranty) => warranty.societyName,
    },
    {
      key: 'name',
      label: 'Nom',
      render: (warranty) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span>{warranty.name}</span>
          {warranty.isBroadcast === false && (
            <span
              className="tw-px-2 tw-py-0.5 tw-bg-amber-100 tw-text-amber-800 tw-text-xs tw-rounded-full tw-font-medium"
              title="Non diffusée : absente de la création de contrat et du catalogue public"
            >
              Non diffusée
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'warrantyType',
      label: 'Type',
      render: (warranty) => (
        <span
          className={`tw-px-2 tw-py-0.5 tw-text-xs tw-rounded-full tw-font-medium tw-whitespace-nowrap ${WARRANTY_TYPE_BADGE_CLASSES[warranty.warrantyType] ?? 'tw-bg-gray-100 tw-text-gray-700'}`}
          title={getWarrantyTypeLabel(warranty.warrantyType)}
        >
          {warranty.warrantyType === WARRANTY_TYPES.EXTENSION ? 'Extension' : getWarrantyTypeLabel(warranty.warrantyType)}
        </span>
      ),
    },
    {
      key: 'duration',
      label: 'Durée',
      render: (warranty) => warranty.warrantyType === WARRANTY_TYPES.EXTENSION ? '—' : getDurationLabel(warranty.duration),
    },
    {
      key: 'kmLimit',
      label: 'Limite KM',
      render: (warranty) => warranty.kmLimit ? warranty.kmLimit.toLocaleString() : '—',
    },
    {
      key: 'financialCap',
      label: 'Plafond financier',
      render: (warranty) => `${warranty.financialCap.toLocaleString()} €`,
    },
    {
      key: 'vehicleType',
      label: 'Type de véhicule',
      render: (warranty) => getVehicleTypeLabel(warranty.vehicleType),
    },
    {
      key: 'partsCount',
      label: 'Pièces couvertes',
      render: (warranty) => warranty.partsCount,
    },
    {
      key: 'assistanceFormulaId',
      label: ASSISTANCE_LABEL,
      // La table rend toute colonne triable par défaut, or l'API valide `sortBy`
      // contre une whitelist `@IsIn` : un clic sur cet en-tête renverrait un 400.
      sortable: false,
      // Pas d'infobulle de formule : la liste ne sert que l'identifiant, et le
      // référentiel des libellés ne vaut pas une requête de plus ici.
      // `!= null` volontaire : une réponse d'API sans le champ ne doit pas
      // compter comme couverte.
      render: (warranty) => <AssistanceBadge hasAssistance={warranty.assistanceFormulaId != null} />,
    },
    {
      key: 'sellingPrice' as const,
      label: 'Prix de vente',
      render: (warranty: any) => {
        if (warranty.warrantyType !== WARRANTY_TYPES.EXTENSION && warranty.isCashPayment && warranty.isMensualisable) {
          if (societyHasStripe) {
            const breakdown = calculateMonthlyBreakdown(warranty.sellingPrice, warranty.duration, warranty.monthlyPlateformFees);
            return (
              <div className="tw-flex tw-flex-col">
                <span className="tw-font-medium tw-text-green-600">
                  {formatPriceTTC(warranty.sellingPrice)}
                </span>
                <span className="tw-text-xs tw-text-gray-500">
                  {breakdown.monthlyClient.toFixed(2)} € × {warranty.duration} mois
                </span>
              </div>
            );
          }
          // Pas de Stripe : afficher uniquement le prix comptant
          return (
            <span className="tw-font-medium tw-text-green-600">
              {formatPriceTTC(warranty.sellingPrice)}
            </span>
          );
        }

        if (warranty.warrantyType !== WARRANTY_TYPES.EXTENSION && !warranty.isCashPayment && warranty.isMensualisable) {
          return (
            <span className="tw-font-medium tw-text-green-600">
              {warranty.sellingPrice.toFixed(2)} € × {warranty.duration} mois
            </span>
          );
        }

        return (
          <span className="tw-font-medium tw-text-green-600">
            {formatPriceTTC(warranty.sellingPrice)}
          </span>
        );
      },
    },
    ...(showTransferPrices ? [{
      key: 'transferPrices' as const,
      label: 'Rétrocession / Achat HT',
      sortable: false,
      render: (warranty: Warranty) => {
        const head = warranty.headTransferPrice ? `${warranty.headTransferPrice.toFixed(2)} €` : '—';
        const member = warranty.memberTransferPrice ? `${warranty.memberTransferPrice.toFixed(2)} €` : '—';
        return `${head} / ${member}`;
      },
    }] : []),
    {
      key: 'isActive',
      label: 'Actif',
      sortable: false,
      render: (warranty) => (
        (isWarrantyPolicyOwner || currentUser?.type === 'INTERNAL') ? (
          <Can permission="warranty.toggle_status" fallback={<span className="tw-text-gray-500 tw-text-xs">—</span>}>
            <div onClick={(e) => e.stopPropagation()}>
              <label className="tw-relative tw-inline-flex tw-items-center tw-cursor-pointer">
                <input
                  type="checkbox"
                  checked={warranty.isActive}
                  onChange={() => handleToggleActive(warranty.uuid, warranty.isActive)}
                  className="tw-sr-only tw-peer"
                />
                <div className="tw-w-7 tw-h-4 tw-bg-gray-200 peer-focus:tw-outline-none peer-focus:tw-ring-1 peer-focus:tw-ring-affinicar-lighter tw-rounded-full tw-peer peer-checked:after:tw-translate-x-full rtl:peer-checked:after:tw--translate-x-full peer-checked:after:tw-border-white after:tw-content-[''] after:tw-absolute after:tw-top-0.5 after:tw-start-0.5 after:tw-bg-white after:tw-border-gray-300 after:tw-border after:tw-rounded-full after:tw-h-3 after:tw-w-3 after:tw-transition-all peer-checked:tw-bg-affinicar-primary"></div>
              </label>
            </div>
          </Can>
        ) : (
          <span className={`tw-inline-block tw-w-2 tw-h-2 tw-rounded-full ${warranty.isActive ? 'tw-bg-green-500' : 'tw-bg-gray-400'}`} />
        )
      ),
    },
  ];

  const rowActions: RowAction<Warranty>[] = [
    ...((isWarrantyPolicyOwner || currentUser?.type === 'INTERNAL') ? [{
      label: 'Couleur',
      render: (warranty: Warranty) => (
        <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ColorPickerPopover
            value={warranty.color}
            onChange={(color: string | null) => {
              updateWarranty.mutate({
                id: warranty.uuid,
                warranty: { color },
              });
            }}
            disabled={!warranty.isActive}
            size="sm"
          />
        </div>
      ),
      onClick: () => {},
      variant: 'secondary' as const,
      permission: 'warranty.edit_color',
      visible: (warranty: Warranty) => warranty.isActive,
    }] : []),
    ...((isWarrantyPolicyOwner || currentUser?.type === 'INTERNAL') ? [{
      label: 'Dupliquer',
      icon: Copy,
      onClick: (warranty: Warranty) => handleDuplicate(warranty.uuid, warranty.name),
      variant: 'secondary' as const,
      permission: 'warranty.duplicate',
    }] : []),
  ];

  const visibleColumns = useMemo(() => {
    let cols = columns;

    if (fixedSocietyId || currentUser?.type === 'EXTERNAL') {
      cols = cols.filter((col) => col.key !== 'societyName');
    }

    // Colonne Type inutile si la société ciblée n'a qu'un seul type de garantie.
    // Basé sur les types réellement présents (facets) et non sur le vendorType,
    // qui ignore les extensions et vaut false sans société sélectionnée.
    if (effectiveFilters.societyId && facets && facets.warrantyTypes.length <= 1) {
      cols = cols.filter((col) => col.key !== 'warrantyType');
    }

    if (!showAssistance) {
      cols = cols.filter((col) => col.key !== 'assistanceFormulaId');
    }

    return cols;
  }, [columns, currentUser, fixedSocietyId, effectiveFilters.societyId, facets, showAssistance]);

  return (
    <div>
      {/*
        Bandeau + bouton : en vue grille aucun en-tête de colonne n'est rendu
        (DataList remplace ListTable), et il n'existe pas de colonne « Ordre »
        en vue tableau. Sans ce bouton, l'utilisateur n'a aucun moyen de
        rétablir le tri qui réactive le glisser-déposer.
      */}
      {isReorderAllowedForUser && reorderBlockedReason && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-mb-2 tw-text-xs tw-text-gray-600 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg">
          <Info className="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-text-gray-400" />
          <span>{reorderBlockedReason}</span>
          <button
            type="button"
            onClick={() => setSortConfig({ sortBy: 'order', sortDirection: 'ASC' })}
            className="tw-ml-auto tw-px-2.5 tw-py-1 tw-font-medium tw-text-affinicar-primary tw-bg-white tw-border tw-border-affinicar-primary tw-rounded hover:tw-bg-affinicar-lighter tw-transition-colors"
          >
            Rétablir l'ordre d'affichage
          </button>
        </div>
      )}
      <DataList
        data={warranties}
        total={total}
        isLoading={isLoading}
        error={error as Error}
        title={hideTitle || fixedSocietyId ? undefined : wording.plural}
        titleIcon={hideTitle || fixedSocietyId ? undefined : Shield}
        embedded={embedded ?? !!fixedSocietyId}
        showCount={!hideTitle && !fixedSocietyId}
        viewTogglePosition={viewTogglePosition}
        customHeaderLeft={fixedSocietyId ? (
          <div className="tw-flex tw-items-center tw-gap-3">
            {activeData?.total != null && (
              <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-green-100 tw-text-green-700">
                {activeData.total} active{activeData.total > 1 ? 's' : ''}
              </span>
            )}
            {inactiveData?.total != null && (
              <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-gray-100 tw-text-gray-700">
                {inactiveData.total} inactive{inactiveData.total > 1 ? 's' : ''}
              </span>
            )}
          </div>
        ) : undefined}
        headerActions={hideTitle ? undefined : (headerActions ?? (
          <div className="tw-flex tw-items-center tw-gap-2">
            {copyFromSocietyId && fixedSocietyId && (
              <button
                onClick={async () => {
                  const confirmed = await confirm({
                    title: `Copier les ${wording.plural.toLowerCase()} du groupe`,
                    message: `Voulez-vous copier toutes les ${wording.plural.toLowerCase()} actives du groupe vers cette société ?`,
                    confirmLabel: 'Copier',
                    cancelLabel: 'Annuler',
                    variant: 'info',
                  });
                  if (!confirmed) return;
                  try {
                    const result = await copyWarranties.mutateAsync({ sourceSocietyId: copyFromSocietyId, targetSocietyId: fixedSocietyId });
                    showNotification('success', `${result.copied} ${wording.singularLower}(s) copiée(s) avec succès`);
                  } catch {
                    showNotification('error', 'Erreur lors de la copie');
                  }
                }}
                disabled={copyWarranties.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-affinicar-primary tw-text-affinicar-primary tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50"
              >
                {copyWarranties.isLoading ? <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" /> : <Copy className="tw-w-4 tw-h-4" />}
                Copier les {wording.plural.toLowerCase()} du groupe
              </button>
            )}
            {isWarrantyPolicyOwner ? (
              <Can permission="warranty.create">
                <button
                  onClick={() => onCreateClick ? onCreateClick() : navigate('/warranties/create')}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-affinicar-dark tw-transition-colors"
                >
                  <Plus className="tw-w-4 tw-h-4" />
                  Nouvelle {wording.singularLower}
                </button>
              </Can>
            ) : currentUser?.type === 'INTERNAL' ? (
              <span className="tw-text-xs tw-text-gray-400 tw-italic">
                La création de {wording.pluralLower} se fait depuis{' '}
                {policyOwnerSociety ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/vendors/${config?.warrantyPolicyOwnerId}`)}
                    className="tw-text-affinicar-primary hover:tw-text-affinicar-dark tw-underline tw-font-medium tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-xs"
                  >
                    {policyOwnerSociety.name}
                  </button>
                ) : (
                  'la société parente'
                )}
              </span>
            ) : null}
          </div>
        ))}
        columns={visibleColumns}
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
        rowClassName={(warranty) => !warranty.isActive ? 'tw-opacity-40' : ''}
        filterSearch={
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
            <FormInput
              value={filters.search || ''}
              onChange={(value) => updateFilter('search', value || undefined)}
              placeholder="Rechercher..."
              size="sm"
            />
          </div>
        }
        filters={hasVisibleFilters ? (
          <div className="tw-flex tw-flex-wrap tw-gap-3 tw-items-end">
            {showSocietyFilter && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1 tw-w-48">
                  <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Société</span>
                  <AutocompleteSelect<SocietyListItem>
                    apiEndpoint="/societies"
                    searchParam="nameFilter"
                    getOptionLabel={(society) => society.name}
                    getOptionValue={(society) => society.uuid}
                    value={filters.societyId || null}
                    onChange={(value) => updateFilter('societyId', value || undefined)}
                    placeholder="Toutes les sociétés"
                    size="xs"
                    renderOption={(society) => (
                      <div>
                        <div className="tw-font-medium">{society.name}</div>
                        {society.rootParentName && (
                          <div className="tw-text-xs tw-text-gray-500">
                            Groupe : {society.rootParentName}
                          </div>
                        )}
                      </div>
                    )}
                    filterActive={true}
                  />
                </div>
                <div className="tw-hidden md:tw-block tw-h-6 tw-w-px tw-bg-gray-300"></div>
              </>
            )}

            {facetWarrantyTypes.length > 1 && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Type de contrat</span>
                  <FilterInlineSelectInput
                    name="warrantyType"
                    label="Type de contrat"
                    value={filters.warrantyType?.toString() || null}
                    onChange={(value) => updateFilter('warrantyType', value ? Number(value) : undefined)}
                    options={facetWarrantyTypes.map((type) => ({
                      value: type.toString(),
                      label: WARRANTY_TYPE_LABELS[type],
                    }))}
                    hideLabel
                  />
                </div>
                <div className="tw-hidden md:tw-block tw-h-6 tw-w-px tw-bg-gray-300"></div>
              </>
            )}

            {facetVehicleTypes.length > 1 && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Type de véhicule</span>
                  <FilterInlineSelectInput
                    name="vehicleType"
                    label="Type de véhicule"
                    value={filters.vehicleType?.toString() || null}
                    onChange={(value) => updateFilter('vehicleType', value ? Number(value) : undefined)}
                    options={facetVehicleTypes.map((type) => ({
                      value: type.toString(),
                      label: VEHICLE_TYPE_LABELS[type],
                    }))}
                    hideLabel
                  />
                </div>
                <div className="tw-hidden md:tw-block tw-h-6 tw-w-px tw-bg-gray-300"></div>
              </>
            )}

            {facetDurations.length > 1 && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Durée</span>
                  <FilterInlineSelectInput
                    name="duration"
                    label="Durée"
                    value={filters.duration?.toString() || null}
                    onChange={(value) => updateFilter('duration', value ? Number(value) : undefined)}
                    options={facetDurations.map((duration) => ({
                      value: duration.toString(),
                      label: DURATION_LABELS[duration],
                    }))}
                    hideLabel
                    allLabel="Toutes"
                  />
                </div>
                <div className="tw-hidden md:tw-block tw-h-6 tw-w-px tw-bg-gray-300"></div>
              </>
            )}

            {currentUser?.type === 'INTERNAL' && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Diffusion</span>
                  <FilterInlineSelectInput
                    name="isBroadcast"
                    label="Diffusion"
                    value={filters.isBroadcast === undefined ? null : String(filters.isBroadcast)}
                    onChange={(value) =>
                      updateFilter('isBroadcast', value ? value === 'true' : undefined)
                    }
                    options={[
                      { value: 'true', label: 'Diffusées' },
                      { value: 'false', label: 'Non diffusées' },
                    ]}
                    hideLabel
                    showAllOption={false}
                  />
                </div>
                <div className="tw-hidden md:tw-block tw-h-6 tw-w-px tw-bg-gray-300"></div>
              </>
            )}

            {showAssistance && (
              <>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <span className="tw-text-xs tw-text-gray-400 tw-font-normal">{ASSISTANCE_LABEL}</span>
                  <FilterInlineSelectInput
                    name="hasAssistance"
                    label={ASSISTANCE_LABEL}
                    value={filters.hasAssistance === undefined ? null : String(filters.hasAssistance)}
                    onChange={(value) =>
                      updateFilter('hasAssistance', value ? value === 'true' : undefined)
                    }
                    options={[
                      { value: 'true', label: 'Oui' },
                      { value: 'false', label: 'Non' },
                    ]}
                    hideLabel
                  />
                </div>
                <div className="tw-hidden md:tw-block tw-h-6 tw-w-px tw-bg-gray-300"></div>
              </>
            )}

            {facetStatuses.length > 1 && (isWarrantyPolicyOwner || currentUser?.type === 'INTERNAL') && (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Statut</span>
                <FilterInlineSelectInput
                  name="isActive"
                  label="Statut"
                  value={filters.isActive === undefined ? null : String(filters.isActive)}
                  onChange={(value) =>
                    updateFilter('isActive', value ? value === 'true' : undefined)
                  }
                  options={[
                    { value: 'true', label: 'Actif' },
                    { value: 'false', label: 'Inactif' },
                  ]}
                  hideLabel
                  showAllOption={false}
                />
              </div>
            )}
          </div>
        ) : undefined}
        activeFiltersCount={activeFiltersCount}
        onResetFilters={hasVisibleFilters ? resetFilters : undefined}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowActions={rowActions}
        onRowClick={(warranty) => handleNavigate(warranty.uuid)}
        getRowKey={(warranty) => warranty.uuid}
        emptyMessage={`Aucune ${wording.singularLower} trouvée`}
        emptyIcon={Shield}
        enableReorder={canReorder}
        onReorder={reorder}
        reorderPermission="warranty.reorder"
        alternativeView={{
          component: (props) => (
            <WarrantiesGridView
              {...props}
              onDuplicate={handleDuplicate}
              onToggleActive={handleToggleActive}
              onCardClick={(uuid) => handleNavigate(uuid)}
              currentUserType={currentUser?.type}
              canReorder={canReorder}
              showTransferPrices={showTransferPrices}
              isWarrantyPolicyOwner={isWarrantyPolicyOwner}
              hideMonthlyPrice={!societyHasStripe}
              showAssistance={showAssistance}
            />
          ),
          icon: Grid,
          label: 'Vue grille',
        }}
      />
      {ConfirmDialogComponent}
    </div>
  );
};
